(function () {
  'use strict';

  // Concurrency guard keyed by item.key (stable Shopify line item identifier)
  const rowLocks = new Map();

  // Normalise option values for comparison: trim whitespace, lowercase
  const normalize = str => str.trim().toLowerCase();

  // ─── Helpers ────────────────────────────────────────────────────────────────

  /**
   * Read the current value of every .cart-item__variant-select in `wrapper`,
   * ordered by data-option-index. Returns an array of strings, e.g. ["30x40cm"].
   */
  function getSelectedOptions(wrapper) {
    const selects = Array.from(wrapper.querySelectorAll('.cart-item__variant-select'))
      .sort((a, b) => parseInt(a.dataset.optionIndex) - parseInt(b.dataset.optionIndex));
    return selects.map(s => s.value);
  }

  /**
   * Restore all selects in `wrapper` to the values in `options` array.
   * Used to revert the UI on failure or no-op.
   */
  function restoreSelectUI(wrapper, options) {
    const selects = Array.from(wrapper.querySelectorAll('.cart-item__variant-select'))
      .sort((a, b) => parseInt(a.dataset.optionIndex) - parseInt(b.dataset.optionIndex));
    selects.forEach((sel, i) => { if (options[i] !== undefined) sel.value = options[i]; });
  }

  /**
   * Find the variant in `variants` whose options array matches `selectedOptions`
   * (case-insensitive, trimmed). Returns the variant object or undefined.
   */
  function findVariant(variants, selectedOptions) {
    return variants.find(v =>
      v.options.every((opt, i) => normalize(opt) === normalize(selectedOptions[i]))
    );
  }

  /**
   * Toggle loading state on the wrapper and its parent row.
   * `bool` true → disables selects (via CSS), fades the row.
   */
  function setRowLoading(wrapper, bool) {
    wrapper.dataset.loading = bool ? 'true' : 'false';
    const row = wrapper.closest('tr') || wrapper.closest('.cart-item');
    if (row) row.dataset.loading = bool ? 'true' : 'false';
  }

  /**
   * Show a toast notification.
   * Delegates to window.HavjoyCore.showToast if available, else falls back to alert.
   */
  function showToast(message, type) {
    if (window.HavjoyCore && typeof window.HavjoyCore.showToast === 'function') {
      window.HavjoyCore.showToast(message, type);
    } else {
      console.warn('[cart-variant-edit]', message);
    }
  }

  // ─── Rollback ────────────────────────────────────────────────────────────────

  /**
   * Best-effort restore of the original line item after a failed update.
   * snapshot: { id, quantity, properties, selling_plan }
   */
  function rollback(snapshot) {
    const item = {
      id: snapshot.id,
      quantity: snapshot.quantity,
      properties: snapshot.properties
    };
    if (snapshot.selling_plan) item.selling_plan = snapshot.selling_plan;

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [item] })
    });
  }

  // ─── Core update flow ────────────────────────────────────────────────────────

  async function updateVariant(wrapper, prevOptions) {
    const lineKey = wrapper.dataset.lineKey;

    // Guard all dataset reads: Liquid may emit empty/missing values
    const currentVariantId = parseInt(wrapper.dataset.variantId) || 0;
    const currentQty = parseInt(wrapper.dataset.quantity) || 1;
    const sellingPlan = wrapper.dataset.sellingPlan || undefined;

    // Read properties from a <script type="application/json"> sibling.
    // Shopify's `item.properties | json` serializes as [[key, val], [key, val], ...]
    // (array of pairs), not {key: val}. Convert accordingly.
    const propertiesScript = wrapper.parentElement.querySelector('.cart-item__properties-data');
    let properties = {};
    if (propertiesScript) {
      try {
        const parsed = JSON.parse(propertiesScript.textContent);
        if (Array.isArray(parsed)) {
          // [[key, val], ...] pairs → plain object
          properties = parsed.length > 0 ? Object.fromEntries(parsed) : {};
        } else if (parsed && typeof parsed === 'object') {
          properties = parsed;
        }
      } catch (e) {
        console.error('[cart-variant-edit] Failed to parse properties:', e);
      }
    }

    const newOptions = getSelectedOptions(wrapper);

    // No-op: selection hasn't actually changed
    if (newOptions.every((v, i) => normalize(v) === normalize(prevOptions[i]))) return;

    // Cache parsed variants on this DOM element. The cache lives for the lifetime of this
    // element — it is reset on each cart re-render (DOM replacement), but helps for rapid
    // successive changes within a single render cycle.
    if (!wrapper._variants) {
      const script = wrapper.parentElement.querySelector('.cart-item__variants-data');
      if (!script) {
        console.error('[cart-variant-edit] variants data script block not found for', lineKey);
        return;
      }
      wrapper._variants = JSON.parse(script.textContent);
    }

    const matchedVariant = findVariant(wrapper._variants, newOptions);

    if (!matchedVariant) {
      // No available variant matches this combination — revert and inform user
      restoreSelectUI(wrapper, prevOptions);
      showToast('This option is currently unavailable.', 'error');
      return;
    }

    if (matchedVariant.id === currentVariantId) return; // same variant, no-op

    setRowLoading(wrapper, true);

    // Request token: detects if DOM was replaced mid-flight (belt-and-suspenders, lock handles most cases)
    const requestId = Symbol();
    wrapper._requestId = requestId;

    const snapshot = {
      id: currentVariantId,
      quantity: currentQty,
      properties,
      selling_plan: sellingPlan,
      options: prevOptions
    };

    // Tracks whether /cart/change.js succeeded (item removed from cart).
    // Rollback is only safe if the item was actually removed first.
    let itemRemoved = false;

    try {
      // Step 1: Remove old line item
      // IMPORTANT: `id` here is the line item key (e.g. "40123456789:abc123"), NOT the variant ID.
      // Using variant ID would remove ALL matching variants — use item.key for precision.
      const changeRes = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: 0 }) // id = line item key
      });
      if (!changeRes.ok) throw Object.assign(new Error(), { status: changeRes.status });
      const changeData = await changeRes.json().catch(() => null);
      if (!changeData) throw new Error('Invalid cart response from /cart/change.js');

      itemRemoved = true; // item is now removed from cart; rollback is safe if needed

      // Guard: if DOM was replaced (or another update changed requestId) while /cart/change.js was in-flight,
      // the item has ALREADY been removed — MUST rollback, not plain return, to prevent cart data loss.
      if (wrapper._requestId !== requestId) {
        await rollback(snapshot).catch(() => {});
        return;
      }

      // Step 2: Add new variant with all properties preserved
      const addPayload = { id: matchedVariant.id, quantity: currentQty, properties };
      if (sellingPlan) addPayload.selling_plan = sellingPlan;

      const addRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [addPayload] })
      });

      if (!addRes.ok) {
        const err = await addRes.json().catch(() => ({}));
        const status = addRes.status;

        if (status === 422) {
          // Inventory too low — inform user and retry with quantity 1
          showToast('Quantity adjusted to 1 due to limited stock.', 'info');
          const retryRes = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [{ ...addPayload, quantity: 1 }] })
          });
          if (!retryRes.ok) throw Object.assign(new Error(err.description || 'Out of stock'), { status: 422 });
        } else {
          throw Object.assign(new Error(err.description || 'Failed to update variant'), { status });
        }
      }

      // Success: record the new selection as last-known-good
      wrapper._currentOptions = newOptions;

    } catch (e) {
      console.error('[cart-variant-edit] update failed:', e);
      // Only rollback if the item was actually removed — otherwise rollback would add a duplicate
      if (itemRemoved) await rollback(snapshot).catch(() => {});
      restoreSelectUI(wrapper, snapshot.options);

      const msg = e.status === 422
        ? 'Not enough stock for that option.'
        : 'Could not update variant. Please try again.';
      showToast(msg, 'error');

    } finally {
      // Always re-render cart UI to match actual cart state, even on failure.
      // CartDrawerController.update() handles the cart drawer AND publishes PUB_SUB_EVENTS.cartUpdate
      // (which CartItems on the /cart page listens to for its own re-render).
      // Fallback: if CartDrawerController is unavailable (e.g. cart drawer not on this page),
      // publish the event directly so CartItems still re-renders.
      setRowLoading(wrapper, false);
      if (window.CartDrawerController && typeof window.CartDrawerController.update === 'function') {
        await window.CartDrawerController.update();
      } else if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'havjoy-cart-variant-edit' });
      }
    }
  }

  // ─── Event handling ──────────────────────────────────────────────────────────

  function handleSelectChange(event) {
    const wrapper = event.target.closest('.cart-item__variant-selects');
    if (!wrapper) return;

    const lineKey = wrapper.dataset.lineKey;
    if (!lineKey) return;

    // Concurrency guard: ignore changes while this row is already being updated
    if (rowLocks.get(lineKey)) return;
    rowLocks.set(lineKey, true);

    // Capture last-known-good options BEFORE async work.
    // _currentOptions is seeded on init() and updated on each success.
    const prevOptions = wrapper._currentOptions || getSelectedOptions(wrapper);

    updateVariant(wrapper, prevOptions)
      .finally(() => rowLocks.delete(lineKey));
  }

  // ─── Initialisation ──────────────────────────────────────────────────────────

  /**
   * Find any uninitialised .cart-item__variant-select elements, attach a change
   * listener that stops propagation (so CartItems.onChange never sees these events),
   * and seed _currentOptions on their parent wrappers.
   *
   * Uses a data attribute flag rather than a JS property so detection works on
   * fresh DOM elements regardless of when the function runs.
   */
  function attachSelectListeners() {
    document.querySelectorAll('.cart-item__variant-select:not([data-ve-init])').forEach(select => {
      select.setAttribute('data-ve-init', '1');
      select.addEventListener('change', (event) => {
        // Stop the event here so it never reaches the CartItems `change` listener,
        // which would call validateQuantity → resetQuantityInput(undefined) → crash.
        event.stopPropagation();
        handleSelectChange(event);
      });
    });

    document.querySelectorAll('.cart-item__variant-selects').forEach(wrapper => {
      if (!wrapper._currentOptions) {
        wrapper._currentOptions = getSelectedOptions(wrapper);
      }
    });
  }

  // Run immediately for elements already in the DOM at script load time.
  attachSelectListeners();

  // Watch for cart re-renders (CartItems.onCartUpdate, CartDrawerController.update, etc.)
  // that add new select elements. MutationObserver fires synchronously after each DOM
  // batch, so new selects are initialised before the user can interact with them.
  new MutationObserver((mutations) => {
    if (mutations.some(m => m.addedNodes.length > 0)) {
      attachSelectListeners();
    }
  }).observe(document.body, { childList: true, subtree: true });

})();
